import './global.css';

import { AppProps } from 'next/app';
import { NextPage } from 'next';
import Head from 'next/head';
import { createClient, defaultExchanges, Provider as UrqlProvider } from 'urql';
import { ubieAuthStorage } from '~/utils/storage/inquiryUbieAppStorage';
import { operationNameExchange } from '~/utils/operationNameExchange';
import { ErrorBoundaryHandler } from '~/components/errors/error-boundary-handler';
import { PageViewWatcher } from '~/components/features/page-view/PageViewWatcher';
import { NextSeo } from 'next-seo';
import { MedicoUserAccount } from '~/components/pages/medico-user-account/MedicoUserAccount';
import { DeveloperBanner } from '~/components/features/developer-banner/DeveloperBanner';
import { medicoUserAuthStorage } from '~/utils/storage/ubieAppStorage';
import { useIsAppointmentPath } from '~/hooks/useIsAppointmentPath';

const urqlClientForMacken = createClient({
  url: process.env.NEXT_PUBLIC_MACKEN_URL as string,
  fetchOptions: () => {
    const { accessToken } = ubieAuthStorage.get() ?? {};
    if (!accessToken) {
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
  },
  exchanges: [operationNameExchange, ...defaultExchanges],
});

const urqlClientForMedinaviBackend = createClient({
  url: process.env.NEXT_PUBLIC_MEDINAVI_BACKEND_URL as string,
  fetchOptions: () => {
    const auth = medicoUserAuthStorage.get();
    if (auth?.authSession.accessToken == null) return {};
    return {
      headers: {
        'X-Ubie-Authorization': `Bearer ${auth.authSession.accessToken}`,
      },
    };
  },
  exchanges: [operationNameExchange, ...defaultExchanges],
});

const App: NextPage<AppProps> = ({ Component, pageProps }) => {
  const isAppointmentPath = useIsAppointmentPath();

  return (
    <>
      <Head>
        <meta name="viewport" content="viewport-fit=cover,width=device-width,initial-scale=1.0,minimum-scale=1.0" />
      </Head>
      <NextSeo
        defaultTitle="スマートフォン問診 by ユビー"
        titleTemplate="%s | ユビー"
        noindex
        openGraph={{
          siteName: 'スマートフォン問診 by ユビー',
          type: 'website',
        }}
      />
      <ErrorBoundaryHandler>
        <UrqlProvider value={isAppointmentPath ? urqlClientForMedinaviBackend : urqlClientForMacken}>
          <PageViewWatcher>
            <main>
              {isAppointmentPath && <DeveloperBanner />}
              <MedicoUserAccount>
                <Component {...pageProps} />
              </MedicoUserAccount>
            </main>
          </PageViewWatcher>
        </UrqlProvider>
      </ErrorBoundaryHandler>
    </>
  );
};

export default App;
